import { addDays, addWeeks, format } from 'date-fns'
import {
	ar,
	cs,
	de,
	el,
	enGB,
	es,
	fi,
	fr,
	frCA,
	he,
	hu,
	it,
	ja,
	nb,
	nl,
	pl,
	pt,
	ptBR,
	ro,
	ru,
	sv,
	th,
	tr,
} from 'date-fns/locale'
import {
	Currency,
	Filters,
	HotelsListResponse,
	SearchItemLocationType,
} from './types'

export const LOCATION_TO_TEXT: { [key: string]: string } = {
	[SearchItemLocationType.COUNTRY]: 'country',
	[SearchItemLocationType.CITY]: 'city',
	[SearchItemLocationType.AIRPORT]: 'airport',
	[SearchItemLocationType.HOTEL]: 'hotel',
	[SearchItemLocationType.REGION]: 'region',
	[SearchItemLocationType.FREEREGION]: 'region',
	[SearchItemLocationType.LM]: 'landmark',
	[SearchItemLocationType.HOOD]: 'hood',
	[SearchItemLocationType.GOOGLE_PLACE]: 'place',
}

export const DEFAULT_CURRENCY = 'USD'
export const DEFAULT_LOCALE = 'en'
const DEFAULT_CURRENCY_OBJ: Currency = {
	label: 'US Dollar',
	flag: 'USA',
	abbreviation: 'USD',
}

export const localeMap: Record<string, Locale> = {
	ar,
	'en-GB': enGB,
	jp: ja,
	pt,
	es,
	de,
	fr,
	'fr-ca': frCA,
	'pt-br': ptBR,
	'es-la': es,
	it,
	he,
	nl,
	gr: el,
	no: nb,
	tr,
	cz: cs,
	hu,
	fi,
	ro,
	pl,
	th,
	sv,
	ru,
}

export const FONT_SIZE = {
	XS: 'xs',
	SM: 'sm',
	BASE: 'base',
	MD: 'md',
	LG: 'lg',
	XL: 'xl',
	'1.5XL': '1.5xl',
	'2XL': '2xl',
	'2.5XL': '2.5xl',
	'2.7XL': '2.7xl',
	'3XL': '3xl',
	'4XL': '4xl',
	'4.5XL': '4.5xl',
	'5XL': '5xl',
	'6XL': '6xl',
} as const

export type FontSize = (typeof FONT_SIZE)[keyof typeof FONT_SIZE]

export const enum FORM_TYPE {
	FRONT = 'FRONT',
	BACK = 'BACK',
}

export const enum PAGE_TYPE {
	MAIN_PAGE = 'MAIN_PAGE',
	RESULTS_PAGE = 'RESULTS_PAGE',
	HOTEL_PAGE = 'HOTEL_PAGE',
}

export const enum INPUT {
	LOCATION = 'LOCATION',
	CALENDAR_FROM = 'CALENDAR_FROM',
	CALENDAR_TO = 'CALENDAR_TO',
	ROOMS_AND_GUESTS = 'ROOMS_AND_GUESTS',
	GUESTS_SELECT = 'GUESTS_SELECT',
	CURRENCY = 'CURRENCY',
	EMAIL_SIGNUP = 'EMAIL_SIGNUP',
	LANGUAGE_SELECT = 'LANGUAGE_SELECT',
	SORT_FILTER = 'SORT_FILTER',
	MAIN_FORM = 'MAIN_FORM',
}

export const enum HOTEL_FORM_INPUT {
	HOTEL_FORM = 'HOTEL_FORM',
	CALENDAR_FROM = 'HOTEL_CALENDAR_FROM',
	CALENDAR_TO = 'HOTEL_CALENDAR_TO',
	ROOMS_AND_GUESTS = 'HOTEL_ROOMS_AND_GUESTS',
}

export const enum SMALL_HEADER_FORM {
	STICKY_HEADER = 'STICKY_HEADER',
}

export type AvailableInputTypes =
	| INPUT
	| HOTEL_FORM_INPUT
	| SMALL_HEADER_FORM
	| FILTER
	| null

export const SINGLE_HOTEL_LOCAL_STORAGE_KEY = 'single-hotel-data'
export const HOTELS_LOCAL_STORAGE_KEY = 'hotels-item'
export const PREV_LANG_LOCAL_STORAGE_KEY = 'prev-lang'
export const MAX_HOTELS_LOCAL_STORAGE_LENGTH = 20
export const REDIRECT_FORM_FRONT_LOCAL_STORAGE_KEY = 'redirect-form-data-front'
export const INIT_DATA_STORAGE_KEY = 'init-data'
export const CONTACT_FORM_SESSION_STORAGE_KEY = 'contact-form-data'
export const COOKIE_NOTICE_STORAGE_KEY = 'show-cookie-notice'
export const GB_EXPERIMENT_STORAGE_KEY = 'gb-experiment-data'
export const UUID_NAMESPACE = '1b671a64-40d5-491e-99b0-da01ff1f3341'
export const DATE_FORMAT_QUERY = 'yyyy-MM-dd'
export const HOTEL_PAGE_MODAL_SESSION_STORAGE_KEY = 'hotel-page-modal'
export const HOTEL_PAGE_SEARCH_STATUS_STORAGE_KEY = 'hotel-page-search-status'

export const FALLBACK_IP_ADDRESS = '34.70.143.70'

export const DEFAULT_CHILD_AGE = 10
export const ROOM_GUESTS_SEPARATOR = '|'
export const DEFAULT_PROVIDER = 'kayak'
export const DEFAULT_TAB = 'front'

export const MD_BREAKPOINT = '(min-width: 768px)'
export const LG_BREAKPOINT = '(min-width: 1024px)'
export const XL_BREAKPOINT = '(min-width: 1280px)'
export const XLG_BREAKPOINT = '(min-width: 1440px)'
export const SM_BREAKPOINT = '(max-width: 767.70px)'
export const TABLET_DOWN = '(max-width: 1279.70px)'
export const LG_BREAKPOINT_DOWN = '(max-width: 1023.70px)'
export const MD_HEIGHT_BREAKPOINT = '(max-height: 890px)'
export const SM_HEIGHT_BREAKPOINT = '(max-height: 768px)'
export const XS_HEIGHT_BREAKPOINT = '(max-height: 580px)'
export const TABLET_VERTICAL = '(min-width: 768px) and (max-width: 1139.70px)'

export const HOTEL_SKELETON_IMAGE = '/images/image-placeholder.webp'
export const FALLBACK_IMAGE = '/images/hotel-image-placeholder.webp'
export const FALLBACK_IMAGE_SVG = '/images/hotel-image-placeholder.svg'
export const FALLBACK_IMAGE_LARGE = '/images/fallback-large.webp'

export const defaultCheckInDate = addWeeks(new Date(), 1)
export const defaultCheckOutDate = addWeeks(new Date(), 2)
export const DEFAULT_STAR_RATING = '0|1|2|3|4|5'
export const DEFAULT_STAR_RATING_ARRAY = [0, 1, 2, 3, 4, 5]
export const DEFAULT_NUMBER_OF_HOTELS = 25
export const DEFAULT_NUMBER_OF_GUESTS = 2
export const DEFAULT_ROOMS_AND_GUESTS = '2|0'

export const DEFAULT_CURRENCY_OBJECT: Currency = {
	label: 'US Dollar',
	flag: 'US',
	abbreviation: 'USD',
}

export const DEFAULT_USER_COUNTRY = 'US'

// Date offset constants for better maintainability
export const HOTEL_DEFAULT_CHECK_IN_WEEKS = 1
export const HOTEL_DEFAULT_STAY_DAYS = 2
export const CITY_DEFAULT_CHECK_IN_WEEKS = 1
export const CITY_DEFAULT_CHECK_OUT_WEEKS = 2

export const DEFAULT_CHECK_IN_DATE = format(
	addWeeks(new Date(), CITY_DEFAULT_CHECK_IN_WEEKS),
	DATE_FORMAT_QUERY
)
export const DEFAULT_CHECK_OUT_DATE = format(
	addWeeks(new Date(), CITY_DEFAULT_CHECK_OUT_WEEKS),
	DATE_FORMAT_QUERY
)

export const DEFAULT_HOTEL_CHECK_IN_DATE = format(
	addWeeks(new Date(), HOTEL_DEFAULT_CHECK_IN_WEEKS),
	DATE_FORMAT_QUERY
)
export const DEFAULT_HOTEL_CHECK_OUT_DATE = format(
	addDays(new Date(DEFAULT_HOTEL_CHECK_IN_DATE), HOTEL_DEFAULT_STAY_DAYS),
	DATE_FORMAT_QUERY
)

export const enum FILTER {
	SORTING = 'SORTING',
	FILTERS = 'FILTERS',
}

export const DEFAULT_ALLOCATION = {
	adults: 2,
	childAge: [] as number[],
}

export const TOP_CURRENCIES: Currency[] = [
	{ label: 'US Dollar', flag: 'US', abbreviation: 'USD' },
	{ label: 'Euro', flag: 'EU', abbreviation: 'EUR' },
	{ label: 'UK Pound', flag: 'GB', abbreviation: 'GBP' },
	{ label: 'Australian Dollar', flag: 'AU', abbreviation: 'AUD' },
	{ label: 'Canadian Dollar', flag: 'CA', abbreviation: 'CAD' },
	{ label: 'Swiss Franc', flag: 'CH', abbreviation: 'CHF' },
	{ label: 'Japanese Yen', flag: 'JP', abbreviation: 'JPY' },
	{ label: 'Brazilian Real', flag: 'BR', abbreviation: 'BRL' },
]

export const OTHER_CURRENCIES: Currency[] = [
	{ label: 'Indian Rupee', flag: 'IN', abbreviation: 'INR' },
	{ label: 'New Zealand Dollar', flag: 'NZ', abbreviation: 'NZD' },
	{ label: 'Singapore Dollar', flag: 'SG', abbreviation: 'SGD' },
	{ label: 'Philippine Peso', flag: 'PH', abbreviation: 'PHP' },
	{ label: 'UAE Dirham', flag: 'AE', abbreviation: 'AED' },
	{ label: 'Thai Baht', flag: 'TH', abbreviation: 'THB' },
	{ label: 'Russian Ruble', flag: 'RU', abbreviation: 'RUB' },
	{ label: 'South African Rand', flag: 'ZA', abbreviation: 'ZAR' },
	{ label: 'Chinese Yuan', flag: 'CN', abbreviation: 'CNY' },
	{ label: 'Hong Kong Dollar', flag: 'HK', abbreviation: 'HKD' },
	{ label: 'Mexican Peso', flag: 'MX', abbreviation: 'MXN' },
	{ label: 'Malaysian Ringgit', flag: 'MY', abbreviation: 'MYR' },
	{ label: 'Argentine Peso', flag: 'AR', abbreviation: 'ARS' },
	{ label: 'Chilean Peso', flag: 'CL', abbreviation: 'CLP' },
	{ label: 'Israeli New Shekel', flag: 'IL', abbreviation: 'ILS' },
	{ label: 'Norwegian Krone', flag: 'NO', abbreviation: 'NOK' },
	{ label: 'Danish Krone', flag: 'DK', abbreviation: 'DKK' },
	{ label: 'Czech Koruna', flag: 'CZ', abbreviation: 'CZK' },
	{ label: 'Indonesian Rupiah', flag: 'ID', abbreviation: 'IDR' },
	{ label: 'Saudi Arabian Riyal', flag: 'SA', abbreviation: 'SAR' },
]

export enum RATING_COLOR {
	LOW = 'score-low',
	MEDIUM = 'score-good',
	HIGH = 'score-high',
}

export enum RATING_THRESHOLD {
	LOW = 4,
	MEDUIM = 8,
}

export const MILES_TO_KM = 1.60934
export const KM_TO_MILES = 0.621371

// 1440x1024 Desktop
// 768x1024 Tablet Vertical
// 1024x768 Tablet Horizontal
// 390x844 Mobile
export const CYPRESS_VIEWPORTS: Record<
	string,
	{ width: number; height: number }
> = {
	DESKTOP: { width: 1440, height: 1024 }, // Desktop
	TABLET_VERTICAL: { width: 768, height: 1024 }, // Tablet Vertical
	TABLET_HORIZONTAL: { width: 1024, height: 768 }, // Tablet Horizontal
	MOBILE: { width: 390, height: 844 }, // Mobile
}

export const MIN_PRICE = 0
export const MAX_PRICE = 1000

export const FACILITIES = [
	{ id: 230, name: 'airportShuttle' },
	{ id: 395, name: 'beachfront' },
	{ id: 47, name: 'casino' },
	{ id: 408, name: 'elevator' },
	{ id: 335, name: 'fitnessCenter' },
	{ id: 856, name: 'kidsClub' },
	{ id: 54, name: 'hotTub' },
	{ id: 77, name: 'parking' },
	// { id: 371, name: 'pool' },
	{ id: 72, name: 'restaurant' },
	{ id: 388, name: 'freeWifi' },
	{ id: 969, name: 'gym' },
	{ id: 527, name: 'familyRooms' },
	{ id: 36, name: 'roomService' },
	{ id: 40, name: 'wheelchairAccessible' },
	{ id: 899, name: 'spaWellnessCenter' },
	{ id: 394, name: 'petsAllowed' },
	{ id: 392, name: 'nonSmokingRooms' },
]

export const FACILITIES_FULL_LIST = [
	...FACILITIES,
	// { id: 1065, name: 'breakfastIncluded' },
]

export const STAY_OPTIONS = [
	{ id: 0, name: 'hotel' },
	{ id: 3, name: 'bedAndBreakfast' },
	{ id: 8, name: 'guestHouse' },
	{ id: 5, name: 'hostel' },
	{ id: 2, name: 'inn' },
	{ id: 1, name: 'motel' },
	{ id: 6, name: 'resort' },
	{ id: 12, name: 'apartment' },
	{ id: 13, name: 'house' },
	{ id: 16, name: 'villa' },
]

export const HOTEL_STYLE = [
	{ id: 10, name: 'family' },
	{ id: 19, name: 'romantic' },
	{ id: 22, name: 'spaWellness' },
	{ id: 3, name: 'beach' },
	{ id: 2, name: 'airport' },
	{ id: 13, name: 'ecoFriendly' },
	{ id: 6, name: 'business' },
]

export const FILTER_TYPES = {
	STARS: 'stars',
	STAY_OPTIONS: 'stayOptions',
	RATING: 'rating',
	FACILITIES: 'facilities',
	PRICE_RANGE: 'priceRange',
	HOTEL_STYLES: 'hotelStyles',
} as const

export const POPULAR_FILTERS = [
	{
		id: 5,
		type: FILTER_TYPES.STARS,
		name: 'fiveStars',
	},
	{
		id: 0,
		type: FILTER_TYPES.STAY_OPTIONS,
		name: 'hotels',
	},
	{
		id: 12,
		type: FILTER_TYPES.STAY_OPTIONS,
		name: 'apartments',
	},
	{
		id: 8,
		type: FILTER_TYPES.RATING,
		name: 'veryGood8Plus',
	},
	// {
	// 	id: 371,
	// 	type: FILTER_TYPES.FACILITIES,
	// 	name: 'pool',
	// },
	// {
	// 	id: 1065,
	// 	type: FILTER_TYPES.FACILITIES,
	// 	name: 'breakfastIncluded',
	// },
	{
		id: 77,
		type: FILTER_TYPES.FACILITIES,
		name: 'parking',
	},
]

export const enum PROPERTY_TYPE {
	HOTEL = 0,
	MOTEL = 1,
	INN = 2,
	BED_AND_BREAKFAST = 3,
	HOSTEL = 5,
	RESORT = 6,
	GUEST_HOUSE = 8,
	APARTMENT = 12,
	HOUSE = 13,
	VILLA = 16,
}

export const STARS: Filters['stars'] = [5, 4, 3, 2, 1]

export const RATING: { [key: number]: string } = {
	0: 'any',
	7: 'good',
	8: 'veryGood',
	9: 'wonderful',
	// 6: 'adequate',
}

export const userAgents = [
	'Mozilla/5.0 (Windows NT 6.1) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/109.0.0.0 Safari/537.36 Edg/109.0.1518.140',
	'Mozilla/5.0 (iPhone; CPU iPhone OS 15_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) CriOS/125.0.6422.80 Mobile/15E148 Safari/604.1',
	'Mozilla/5.0 (Linux; Android 10; K) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/110.0.0.0 Mobile Safari/537.36',
	'Mozilla/5.0 (iPad; CPU OS 15_8 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) GSA/333.0.671582647 Mobile/15E148 Safari/604.1',
	'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/127.0.0.0 Safari/537.36 GLS/100.10.9820.99',
	'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_6) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/14.1.2 Safari/605.1.15',
	'Mozilla/5.0 (Linux; Android 9; KFKAWI) AppleWebKit/537.36 (KHTML, like Gecko) Silk/126.6.2 like Chrome/126.0.6478.238 Safari/537.36',
	'Mozilla/5.0 (Linux; Android 14; SAMSUNG SM-A057F) AppleWebKit/537.36 (KHTML, like Gecko) SamsungBrowser/22.0 Chrome/111.0.5563.116 Mobile Safari/537.36',
	'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/92.0.4515.107 Safari/537.36',
	'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Mobile/15E148 Safari/604.1',
	'Mozilla/5.0 (Windows NT 6.3; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/127.0.9703.64 Safari/537.36',
	'Mozilla/5.0 (Windows NT 10.0.0; Win64; x64; ) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/125.0.6422.113 Chrome/125.0.6422.113 Not.A/Brand/24 Safari/537.36',
	'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/101.0.4951.54 Safari/537.36 Edg/101.0.1210.39',
	'Mozilla/5.0 (Windows NT 6.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/128.0.5658.43 Safari/537.36',
	'Mozilla/5.0 (iPhone; CPU iPhone OS 18_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) GSA/333.0.671582647 Mobile/15E148 Safari/604.1',
	'Mozilla/5.0 (Linux; Android 10; EVE-LX9 Build/HUAWEIEVE-LX9; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/88.0.4324.93 Mobile Safari/537.36',
	'Mozilla/5.0 (iPhone; CPU iPhone OS 17_7 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.7 Mobile/15E148 Safari/604.1',
	'Mozilla/5.0 (Linux; Android 10; JNY-LX1) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/103.0.0.0 Mobile Safari/537.36',
]

export const EMPTY_HOTELS_RESPONSE: HotelsListResponse = {
	hotels: [],
	page: 0,
	pages: 0,
	totalResults: 0,
	complete: true,
	refreshInterval: 0,
	searchParams: {},
	lowestTotalRate: 0,
	highestTotalRate: 0,
	lowestNightlyRate: 0,
	highestNightlyRate: 0,
}

export const EUROPE_DESTINATIONS = [
	{
		destination: 'paris',
		image: '/images/destinations/europe/paris.webp',
		price: 120,
		url: 'france/ile-de-france/paris',
		placeId: 37687,
	},
	{
		destination: 'london',
		image: '/images/destinations/europe/london.webp',
		price: 180,
		url: 'united-Kingdom/england/london',
		placeId: 29230,
	},
	{
		destination: 'rome',
		image: '/images/destinations/europe/rome.webp',
		price: 96,
		url: 'italy/lazio/rome',
		placeId: 42802,
	},
	{
		destination: 'amsterdam',
		image: '/images/destinations/europe/amsterdam.webp',
		price: 145,
		url: 'netherlands/north-holland/amsterdam',
		placeId: 31835,
	},
]

export const US_DESTINATIONS = [
	{
		destination: 'newYork',
		image: '/images/destinations/us/new-york.webp',
		price: 320,
		url: 'united-states/new-york/new-york',
		placeId: 59560,
	},
	{
		destination: 'lasVegas',
		image: '/images/destinations/us/las-vegas.webp',
		price: 280,
		url: 'united-states/nevada/las-vegas',
		placeId: 61746,
	},
	{
		destination: 'miami',
		image: '/images/destinations/us/miami.webp',
		price: 260,
		url: 'united-states/florida/miami',
		placeId: 53912,
	},
	{
		destination: 'honolulu',
		image: '/images/destinations/us/honolulu.webp',
		price: 350,
		url: 'united-states/hawaii/honolulu',
		placeId: 62566,
	},
]

export const COOKIE_CONSENT_COUNTRIES = ['FR', 'MD', 'IL']

export const REDIRECT_PARTNERS = { BOOKING: 'b', KAYAK: 'k' }
export const BLOG_POSTS_PER_PAGE = 6

/** Value of pt param that triggers animated passthrough */
export const PT_ANIMATED_PASSTHROUGH = '2'

export const REDIRECT_TIMINGS = {
	/** Duration of the "searching" animation phase */
	MIN_SEARCH_PHASE_MS: 3000,

	/** Time to show the "found" message before redirection */
	MIN_FOUND_PHASE_MS: 1000,

	/** Maximum time to wait for a partner redirect before showing an error */
	MAX_REDIRECT_WAIT_MS: 30000,

	/**
	 * Minimum total time the user should spend on the redirect page when pt=2
	 * this ensures they see both the "searching" and "found" phases.
	 * Normally this is MIN_SEARCH_PHASE_MS + MIN_FOUND_PHASE_MS.
	 */
	MIN_TOTAL_ANIMATION_MS: 4000,
} as const

// Feature flags
export const ENABLE_EMAIL_SIGNUP = false
export const ENABLE_KLAVIYO_EVENTS = false
